import { Alert, Box, LinearProgress } from '@mui/material';

import {
  CustomerRegistrationForm,
  DepartmentResponse,
  FormValidationState,
  NoharaRepresentative,
  Staff,
} from '../types';
import { ConstructionCompanyRegistrationForm } from '../types/company.types';

import { RepresentativesSection } from './representatives-section';
import { FormActionFooter } from './common/form-action-footer';

export interface RegisterFormProps {
  validationState: FormValidationState;
  children: React.ReactNode;
  formData: CustomerRegistrationForm | ConstructionCompanyRegistrationForm;
  addRepresentative: () => void;
  removeRepresentative: (id: string) => void;
  updateRepresentative: (id: string, field: keyof NoharaRepresentative, value: string) => void;
  getSalesPersonOptions: (departmentId: string) => Staff[];
  getFieldError: (field: string) => string | undefined;
  isFieldValid: (field: string) => boolean;
  canAddRepresentative: boolean;
  departments: DepartmentResponse[];
  departmentsLoading: boolean;
  staffsLoading: boolean;
  handleDepartmentChange: (departmentId: string) => void;
  handleCancel: () => void;
  handleFormSubmit: () => void;
  isSubmitDisabled: boolean;
  hasNavigationPanel?: boolean;
}

export const RegisterForm = ({
  validationState,
  formData,
  addRepresentative,
  removeRepresentative,
  updateRepresentative,
  getSalesPersonOptions,
  getFieldError,
  isFieldValid,
  canAddRepresentative,
  departments,
  departmentsLoading,
  staffsLoading,
  handleDepartmentChange,
  handleCancel,
  handleFormSubmit,
  isSubmitDisabled,
  children,
  hasNavigationPanel,
}: RegisterFormProps) => {
  return (
    <>
      {/* Loading Indicator */}
      {validationState.isSubmitting && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}
      {/* General Error Messages */}
      {validationState.errors.some((error) => error.field === 'general') && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {validationState.errors.find((error) => error.field === 'general')?.message}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {children}

        <RepresentativesSection
          representatives={formData.representatives}
          onAddRepresentative={addRepresentative}
          onRemoveRepresentative={removeRepresentative}
          onUpdateRepresentative={updateRepresentative}
          getSalesPersonOptions={getSalesPersonOptions}
          getFieldError={getFieldError}
          isFieldValid={isFieldValid}
          canAddRepresentative={canAddRepresentative}
          departments={departments}
          departmentsLoading={departmentsLoading}
          staffsLoading={staffsLoading}
          onDepartmentChange={handleDepartmentChange}
        />

        <FormActionFooter
          isSubmitting={validationState.isSubmitting}
          isSubmitDisabled={isSubmitDisabled}
          onCancel={handleCancel}
          onSubmit={handleFormSubmit}
          hasNavigationPanel={hasNavigationPanel}
        />
      </Box>
    </>
  );
};
